import React, { useState, useRef } from "react";
import SignatureCanvas from "react-signature-canvas";
import {
    Upload, Trash2, RotateCcw, Check,
    Image as ImageIcon, PenLine, Download, Move,
    ChevronLeft,
    ChevronRight,
    X,
    PenTool,
    TypeIcon
} from "lucide-react";

const STEPS = ["Upload Document", "Create Signature", "Set Position", "Download"];

const COLORS = [
    { hex: "#1a1a2e", label: "Dark" },
    { hex: "#145A43", label: "Forest" },
    { hex: "#1565C0", label: "Blue" },
    { hex: "#c0392b", label: "Red" },
];

export default function SignDocumentSelf() {
    const [step, setStep] = useState(0);
    const [selectedFile, setSelectedFile] = useState(null);
    const [rawFile, setRawFile] = useState(null);
    const [fileUrl, setFileUrl] = useState(null);
    const [sigTab, setSigTab] = useState("draw"); // Options: "draw", "type", "image"
    const [drawColor, setDrawColor] = useState("#1a1a2e");
    const [brushSize, setBrushSize] = useState(2);
    const [typedSig, setTypedSig] = useState("");
    const [downloaded, setDownloaded] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);

    const [capturedSigUrl, setCapturedSigUrl] = useState(null);
    const [sigPos, setSigPos] = useState({ x: 120, y: 180 });

    const [previewDimensions, setPreviewDimensions] = useState({ width: 420, height: 588 });

    const sigCanvasRef = useRef(null);
    const fileInputRef = useRef(null);
    const sigImageInputRef = useRef(null); // Ref for signature image upload
    const docPreviewRef = useRef(null);

    const generateTextSigImage = () => {
        const canvas = document.createElement("canvas");
        canvas.width = 280;
        canvas.height = 100;
        const ctx = canvas.getContext("2d");
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.font = "italic bold 32px Georgia, serif";
        ctx.fillStyle = "#1a1a2e";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(typedSig || "Signed", canvas.width / 2, canvas.height / 2);
        return canvas.toDataURL("image/png");
    };

    const saveCanvasData = () => {
        if (sigTab === "draw" && sigCanvasRef.current && !sigCanvasRef.current.isEmpty()) {
            const rawCanvas = sigCanvasRef.current.getCanvas();
            if (rawCanvas) {
                setCapturedSigUrl(rawCanvas.toDataURL('image/png'));
            }
        } else if (sigTab === "type") {
            setCapturedSigUrl(generateTextSigImage());
        }
    };

    const handleZoneClick = () => {
        if (fileInputRef.current) fileInputRef.current.click();
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setRawFile(file);
            setSelectedFile({
                name: 'iki-signer-doc-' + file.name,
                size: (file.size / 1024).toFixed(0) + " KB",
                type: file.type
            });
            setFileUrl(URL.createObjectURL(file));
            setStep(1);
        }
    };

    const handleSigImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (event) => {
                setCapturedSigUrl(event.target.result);
            };
            reader.readAsDataURL(file);
        }
    };

    function clearSig() {
        if (sigCanvasRef.current) sigCanvasRef.current.clear();
        setCapturedSigUrl(null);
    }

    function undoSig() {
        if (!sigCanvasRef.current) return;
        const data = sigCanvasRef.current.toData();
        if (data.length > 0) {
            data.pop();
            sigCanvasRef.current.fromData(data);
        }
    }

    const handlePageInteraction = (e) => {
        if (!docPreviewRef.current) return;
        const rect = docPreviewRef.current.getBoundingClientRect();

        setPreviewDimensions({ width: rect.width, height: rect.height });

        const isTouch = e.type.startsWith("touch");
        const clientX = isTouch ? e.touches[0].clientX : e.clientX;
        const clientY = isTouch ? e.touches[0].clientY : e.clientY;

        let clickX = clientX - rect.left - 70;
        let clickY = clientY - rect.top - 25;

        clickX = Math.max(0, Math.min(clickX, rect.width - 140));
        clickY = Math.max(0, Math.min(clickY, rect.height - 50));

        setSigPos({ x: Math.round(clickX), y: Math.round(clickY) });
    };

    const captureDimensionsOnNext = () => {
        if (step === 2 && docPreviewRef.current) {
            const rect = docPreviewRef.current.getBoundingClientRect();
            setPreviewDimensions({ width: rect.width, height: rect.height });
        }
    };

    const handleDownload = async () => {
        if (!capturedSigUrl && sigTab === "type") {
            saveCanvasData();
        }

        setIsProcessing(true);

        try {
            if (selectedFile && !selectedFile.type.includes("pdf")) {
                const img = new window.Image();
                img.src = fileUrl;
                img.onload = () => {
                    const canvas = document.createElement("canvas");
                    canvas.width = img.naturalWidth;
                    canvas.height = img.naturalHeight;
                    const ctx = canvas.getContext("2d");

                    ctx.drawImage(img, 0, 0);

                    const scaleX = img.naturalWidth / previewDimensions.width;
                    const scaleY = img.naturalHeight / previewDimensions.height;

                    const sigImg = new window.Image();
                    sigImg.src = capturedSigUrl || generateTextSigImage();
                    sigImg.onload = () => {
                        ctx.drawImage(
                            sigImg,
                            sigPos.x * scaleX,
                            sigPos.y * scaleY,
                            140 * scaleX,
                            50 * scaleY
                        );

                        const link = document.createElement("a");
                        link.download = `signed_${selectedFile.name}`;
                        link.href = canvas.toDataURL("image/png");
                        link.click();

                        setDownloaded(true);
                        setIsProcessing(false);
                    };
                };
            }
            else if (selectedFile && selectedFile.type.includes("pdf")) {
                if (!window.PDFLib) {
                    await new Promise((resolve) => {
                        const script = document.createElement("script");
                        script.src = "https://unpkg.com/pdf-lib@1.17.1/dist/pdf-lib.min.js";
                        script.onload = resolve;
                        document.head.appendChild(script);
                    });
                }

                const { PDFDocument } = window.PDFLib;
                const existingPdfBytes = await rawFile.arrayBuffer();
                const pdfDoc = await PDFDocument.load(existingPdfBytes);
                const pages = pdfDoc.getPages();
                const firstPage = pages[0];
                const { width, height } = firstPage.getSize();

                const scaleX = width / previewDimensions.width;
                const scaleY = height / previewDimensions.height;

                const signatureImageBytes = await fetch(capturedSigUrl || generateTextSigImage()).then(res => res.arrayBuffer());
                const signatureImage = await pdfDoc.embedPng(signatureImageBytes);

                const finalX = sigPos.x * scaleX;
                const finalY = height - (sigPos.y * scaleY) - (50 * scaleY);

                firstPage.drawImage(signatureImage, {
                    x: finalX,
                    y: finalY,
                    width: 140 * scaleX,
                    height: 50 * scaleY,
                });

                const pdfBytes = await pdfDoc.save();
                const blob = new Blob([pdfBytes], { type: "application/pdf" });
                const link = document.createElement("a");
                link.href = URL.createObjectURL(blob);
                link.download = `signed_${selectedFile.name}`;
                link.click();

                setDownloaded(true);
                setIsProcessing(false);
            }
        } catch (err) {
            console.error("Signing failed: ", err);
            setIsProcessing(false);
            alert("Error rendering output layout streams.");
        }
    };

    const btnBase =
        "flex items-center gap-2 py-2 px-4 rounded-full border border-[#2F8F6F]/25 bg-white/5 text-[#D7C7A1] text-xs font-bold transition-all duration-200 hover:bg-[#2F8F6F]/20 hover:border-[#2F8F6F] hover:text-white";

    return (
        <div className="min-h-screen bg-[#0F0F0F] text-white flex flex-col font-sans pb-12">
            {/* Header Area */}
            <div className="bg-gradient-to-br from-[#0B3D2E] via-[#145A43] to-[#0B3D2E] border-b border-[#2F8F6F]/20 px-6 py-12 md:py-16 shadow-lg w-full">
                <div className="max-w-2xl mx-auto flex flex-col justify-center items-center text-center space-y-3">
                    <h1 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight bg-clip-text text-transparent bg-gradient-to-b from-white to-[#D7C7A1]">
                        Sign Your Document
                    </h1>
                    <p className="text-[#D7C7A1] text-xs sm:text-sm max-w-md font-medium leading-relaxed">
                        Upload your file, structure your layout, position elements, and finish entirely local.
                    </p>
                </div>
            </div>

            <div className="w-full max-w-3xl mx-auto p-4 md:p-8">
                {/* Step Pills */}
                <div className="flex flex-wrap gap-2 mb-6 justify-center">
                    {STEPS.map((label, i) => (
                        <button
                            key={i}
                            onClick={() => {
                                if (i > 0 && !selectedFile) return;
                                if (step === 1) saveCanvasData();
                                captureDimensionsOnNext();
                                setStep(i);
                            }}
                            disabled={i > 0 && !selectedFile}
                            className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold border transition-all duration-200 ${i === step
                                ? "bg-[#2F8F6F]/20 border-[#2F8F6F] text-[#D7C7A1]"
                                : i < step
                                    ? "bg-[#2F8F6F]/10 border-[#2F8F6F]/30 text-[#2F8F6F]"
                                    : "bg-white/5 border-[#2F8F6F]/10 text-[#444] cursor-not-allowed"
                                }`}
                        >
                            <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black ${i < step ? "bg-[#2F8F6F] text-white" : i === step ? "bg-[#2F8F6F] text-white" : "bg-white/5 text-[#444]"
                                }`}>
                                {i < step ? <Check size={10} /> : i + 1}
                            </span>
                            {label}
                        </button>
                    ))}
                </div>

                {/* ── STEP 0: Upload ── */}
                {step === 0 && (
                    <div className="rounded-2xl border border-[#2F8F6F]/20 bg-[#0B3D2E]/15 p-6">
                        <div className="flex items-center gap-2 text-[#D7C7A1] text-sm font-bold mb-4">
                            <Upload size={16} /> Upload document to sign
                        </div>

                        <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleFileChange}
                            accept="application/pdf,image/*"
                            className="hidden"
                        />

                        <button
                            onClick={handleZoneClick}
                            className="w-full border-2 border-dashed border-[#2F8F6F]/40 rounded-xl p-12 text-center bg-[#2F8F6F]/5 hover:bg-[#2F8F6F]/10 hover:border-[#2F8F6F] transition-all duration-200 cursor-pointer"
                        >
                            <Upload size={44} className="text-[#2F8F6F] mx-auto mb-3" />
                            <h3 className="text-base font-bold text-white mb-1">Select file to self-sign</h3>
                            <p className="text-xs text-[#666] mb-4">Click to browse or drop PDF/Images</p>
                            <div className="flex gap-2 justify-center">
                                {["PDF", "PNG", "JPG"].map((f) => (
                                    <span key={f} className="bg-[#2F8F6F]/15 border border-[#2F8F6F]/30 rounded-full px-3 py-1 text-[11px] text-[#2F8F6F] font-bold">
                                        {f}
                                    </span>
                                ))}
                            </div>
                        </button>

                        {selectedFile && (
                            <div className="mt-5 flex flex-row items-center justify-between gap-3 bg-white/5 border border-[#2F8F6F]/20 rounded-xl p-4">
                                <div className="text-left flex flex-row items-center gap-1">
                                    <span className="text-3xl">📄</span>
                                    <div className="font-bold text-xs md:text-sm text-white">
                                        {selectedFile?.name
                                            ? selectedFile.name.length > 35
                                                ? `${selectedFile.name.slice(0, 15)}.....${selectedFile.name.slice(-15)}`
                                                : selectedFile.name
                                            : "Document.pdf"
                                        }
                                    </div>
                                    <div className="text-xs text-[#666] w-[10%]">{selectedFile.size}</div>
                                </div>
                                <span className="rounded-full p-2 bg-red-500/50 outline outline-red-500/70 cursor-pointer" onClick={() => setSelectedFile(null)}>
                                    <X size={18} />
                                </span>
                            </div>
                        )}
                    </div>
                )}

                {/* ── STEP 1: Create Signature ── */}
                {step === 1 && (
                    <div className="rounded-2xl border border-[#2F8F6F]/20 bg-[#0B3D2E]/15 p-6">
                        <div className="flex items-center gap-2 text-[#D7C7A1] text-sm font-bold mb-4">
                            <PenLine size={16} /> Create your signature
                        </div>

                        <div className="flex border border-[#2F8F6F]/25 rounded-xl overflow-hidden mb-5">
                            {["draw", "type", "image"].map((t) => (
                                <button
                                    key={t}
                                    onClick={() => {
                                        setSigTab(t);
                                        if (t !== "image") setCapturedSigUrl(null);
                                    }}
                                    className={`flex-1 py-2 text-xs font-bold transition-all duration-200 capitalize ${sigTab === t ? "bg-[#2F8F6F]/20 text-[#D7C7A1]" : "text-[#666] hover:text-[#aaa]"
                                        }`}
                                >
                                    {t === "draw" ?
                                        <span className="flex flex-row gap-2 justify-center align-middle">
                                            <PenTool size={18} />
                                            Draw
                                        </span>
                                        : t === "type" ?
                                            <span className="flex flex-row gap-2 justify-center align-middle">
                                                <TypeIcon size={18} />
                                                Type Initial
                                            </span>
                                            : <span className="flex flex-row gap-2 justify-center align-middle">
                                                <ImageIcon size={18} />
                                                Upload
                                            </span>}
                                </button>
                            ))}
                        </div>

                        {sigTab === "draw" && (
                            <div>
                                <div className="flex gap-4 items-center mb-3">
                                    <span className="text-xs text-[#888] font-bold">Ink Color:</span>
                                    <div className="flex gap-2">
                                        {COLORS.map(({ hex }) => (
                                            <button
                                                key={hex}
                                                onClick={() => setDrawColor(hex)}
                                                style={{ background: hex }}
                                                className={`w-6 h-6 rounded-full border-2 ${drawColor === hex ? "border-[#D7C7A1]" : "border-transparent"}`}
                                            />
                                        ))}
                                    </div>
                                </div>
                                <div className="border border-[#2F8F6F]/30 rounded-xl bg-white h-40">
                                    <SignatureCanvas
                                        ref={sigCanvasRef}
                                        penColor={drawColor}
                                        minWidth={brushSize}
                                        maxWidth={brushSize + 1.5}
                                        canvasProps={{ className: "w-full h-full cursor-crosshair", style: { touchAction: "none" } }}
                                    />
                                </div>
                                <div className="flex gap-2 mt-3">
                                    <button onClick={clearSig} className={btnBase}><Trash2 size={14} /> Clear</button>
                                    <button onClick={undoSig} className={btnBase}><RotateCcw size={14} /> Undo</button>
                                </div>
                            </div>
                        )}

                        {sigTab === "type" && (
                            <div>
                                <input
                                    type="text"
                                    placeholder="Type your name..."
                                    value={typedSig}
                                    onChange={(e) => setTypedSig(e.target.value)}
                                    className="w-full bg-white/5 border border-[#2F8F6F]/30 rounded-lg px-4 py-2 text-sm text-white outline-none focus:border-[#2F8F6F] mb-4"
                                />
                                <div className="bg-white rounded-xl p-4 text-[#145A43] font-serif text-4xl text-center min-h-[80px] flex items-center justify-center select-none">
                                    {typedSig || "Your Signature"}
                                </div>
                            </div>
                        )}

                        {sigTab === "image" && (
                            <div className="text-center">
                                <input
                                    type="file"
                                    ref={sigImageInputRef}
                                    onChange={handleSigImageChange}
                                    accept="image/png"
                                    className="hidden"
                                />
                                {!capturedSigUrl ? (
                                    <button
                                        onClick={() => sigImageInputRef.current?.click()}
                                        className="w-full border border-dashed border-[#2F8F6F]/40 rounded-xl p-8 bg-[#2F8F6F]/5 hover:bg-[#2F8F6F]/10 transition-all duration-200 text-center cursor-pointer"
                                    >
                                        <ImageIcon size={28} className="text-[#2F8F6F] mx-auto mb-2" />
                                        <span className="text-xs font-bold text-[#D7C7A1]">Select Transparent Signature PNG</span>
                                    </button>
                                ) : (
                                    <div className="flex flex-col items-center gap-3">
                                        <div className="bg-white rounded-xl p-4 max-w-[280px] h-28 flex items-center justify-center border border-[#2F8F6F]/30 shadow-inner">
                                            <img src={capturedSigUrl} alt="Uploaded Signature" className="max-w-full max-h-full object-contain" />
                                        </div>
                                        <button
                                            onClick={() => setCapturedSigUrl(null)}
                                            className="text-xs font-bold text-red-400 hover:text-red-500 flex items-center gap-1 transition"
                                        >
                                            <Trash2 size={12} /> Remove and Change Image
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* ── STEP 2: Set Position ── */}
                {step === 2 && (
                    <div className="rounded-2xl border border-[#2F8F6F]/20 bg-[#0B3D2E]/15 p-6 text-center">
                        <div className="flex items-center justify-between text-[#D7C7A1] text-sm font-bold mb-4">
                            <div className="flex items-center gap-2 text-left"><Move size={16} /> Tap/Click inside the document workspace to place signature</div>
                        </div>

                        <div className="flex justify-center bg-black/20 p-2 sm:p-4 rounded-xl border border-white/5">
                            <div
                                ref={docPreviewRef}
                                onClick={handlePageInteraction}
                                onTouchStart={handlePageInteraction}
                                className="relative w-full max-w-[420px] aspect-[1/1.4] bg-white border border-gray-300 shadow-md rounded overflow-hidden cursor-crosshair transition-all"
                            >
                                {selectedFile?.type.includes("pdf") ? (
                                    <object data={fileUrl} type="application/pdf" className="w-full h-full pointer-events-none" />
                                ) : (
                                    <div style={{ backgroundImage: `url(${fileUrl})` }} className="w-full h-full bg-contain bg-no-repeat bg-center pointer-events-none" />
                                )}

                                {/* Signature Position Box Container */}
                                <div
                                    style={{ left: `${sigPos.x}px`, top: `${sigPos.y}px`, width: '140px', height: '50px' }}
                                    className="absolute border-2 border-[#2F8F6F] bg-[#2F8F6F]/20 rounded flex items-center justify-center p-1 pointer-events-none transition-all shadow-md select-none group z-50"
                                >
                                    {/* NEW: Placed the Lucide Move icon directly on the layer badge */}
                                    {/* <div className="absolute top-0.5 right-0.5 bg-[#2F8F6F] text-white rounded-bl p-0.5 shadow-sm opacity-90">
                                        <Move size={10} className="stroke-[2.5]" />
                                    </div> */}

                                    {(sigTab === "draw" || sigTab === "image") && capturedSigUrl && (
                                        <img src={capturedSigUrl} alt="Signature" className="w-full h-full object-contain pointer-events-none" />
                                    )}
                                    {sigTab === "type" && <span className="text-[#1a1a2e] font-serif text-xs sm:text-sm tracking-tight truncate pointer-events-none font-bold">{typedSig || "Signed"}</span>}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* ── STEP 3: Finalize and Download ── */}
                {step === 3 && (
                    <div className="rounded-2xl border border-[#2F8F6F]/20 bg-[#0B3D2E]/15 p-6 relative">
                        <div className="flex items-center gap-2 text-[#D7C7A1] text-sm font-bold mb-5"><Check size={16} /> Finalize document</div>
                        <div className="flex flex-col gap-4">
                            <div className="bg-[#2F8F6F]/07 border border-[#2F8F6F]/20 rounded-xl p-4 relative">
                                <div className="text-[11px] text-[#888] font-bold uppercase tracking-wide mb-2">Signed Document</div>
                                <div className="flex items-center gap-3 relative w-full">
                                    <span className="text-2xl">📄</span>
                                    <div className="text-left">
                                        <div className="font-bold text-xs md:text-sm text-white">
                                            {selectedFile?.name
                                                ? selectedFile.name.length > 35
                                                    ? `${selectedFile.name.slice(0, 15)}.....${selectedFile.name.slice(-15)}`
                                                    : selectedFile.name
                                                : "Document.pdf"
                                            }
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <button
                                onClick={handleDownload}
                                disabled={isProcessing}
                                className={`w-full py-4 rounded-xl font-black text-sm tracking-wide transition-all duration-300 flex flex-row items-center justify-center gap-2 bg-gradient-to-br from-[#2F8F6F] to-[#145A43] text-white hover:opacity-90 hover:-translate-y-0.5`}
                            >
                                {isProcessing ? "Rendering..." : downloaded ? <><Check size={18} /> Download Again</> : <><Download size={18} /> Download Signed Document</>}
                            </button>
                            <button
                                onClick={() => {
                                    window.location.reload()
                                }}
                                disabled={isProcessing}
                                className={`w-full py-4 rounded-xl font-black text-sm tracking-wide transition-all duration-300 flex items-center justify-center gap-2 border border-[#2F8F6F]`}
                            >
                                <PenLine size={18} />
                                Sign Another Document
                            </button>
                        </div>
                    </div>
                )}

                {/* Navigation Controls */}
                <div className="flex items-center justify-between mt-5">
                    <button
                        onClick={() => setStep((s) => Math.max(0, s - 1))}
                        className={`${btnBase} ${step === 0 ? "opacity-0 pointer-events-none" : ""}`}
                    >
                        <ChevronLeft size={18} />
                        Back
                    </button>
                    {step < STEPS.length - 1 && (
                        <button
                            onClick={() => {
                                if (step === 1) saveCanvasData();
                                captureDimensionsOnNext();
                                setStep((s) => Math.min(STEPS.length - 1, s + 1));
                            }}
                            disabled={!selectedFile}
                            className={`${btnBase} ${!selectedFile ? "opacity-40 cursor-not-allowed" : ""}`}
                        >
                            Next <ChevronRight size={18} />
                        </button>
                    )}
                </div>
            </div>

            <div className="text-gray-400 flex w-full justify-center text-xs md:text-md text-end bg-[#0F0F0F] pb-10">
                <p>Copyright © 2025 Rizki Makruf. All rights reserved.</p>
            </div>
        </div>
    );
}